import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  KbLifecycleCreate,
  KbLifecycleFilter,
  KbLifecycleRecord,
  KbLifecycleUpdate,
  KbMetrics,
  KbWorkflowState,
} from './kb.types';

// Cloud manager scope. Distinct from the legacy flat commons KB editor
// (itsm-commons `/itsm/commons/kb`) — this is the full lifecycle library.
const BASE = '/users/v1/itsm/kb';
const KEY = ['kb-lifecycle'] as const;

export function useKbLifecycle(filter: KbLifecycleFilter = {}) {
  const params = new URLSearchParams();
  if (filter.workflow_state) params.set('workflow_state', filter.workflow_state);
  if (filter.class_code) params.set('class_code', filter.class_code);
  const qs = params.toString();
  return useQuery({
    queryKey: [...KEY, filter],
    queryFn: () => api.get<KbLifecycleRecord[]>(`${BASE}${qs ? `?${qs}` : ''}`),
    refetchInterval: 60_000,
  });
}

export function useKbLifecycleArticle(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<KbLifecycleRecord>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useKbMetrics(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'metrics', id],
    queryFn: () => api.get<KbMetrics>(`${BASE}/${id}/metrics`),
    enabled: !!id,
  });
}

export function useCreateKbLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: KbLifecycleCreate) => api.post<KbLifecycleRecord>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateKbLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: KbLifecycleUpdate & { id: string }) =>
      api.post<KbLifecycleRecord>(`${BASE}/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Lifecycle transition (draft→review→published→retired, review→draft rework).
 * Gated to maintenance-Admin server-side; the 400/409 surfaces on the caller. */
export function useTransitionKb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KbWorkflowState }) =>
      api.post<KbLifecycleRecord>(`${BASE}/${id}/transition`, { to }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
